import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  HeaderTemplate,
  Typography,
} from 'web-library';
import dayjs from 'dayjs';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { SearchDate } from '../../../models';
import PaymentReportSearchAndFilter from './PaymentReportSearchAndFilter';
import { usePaymentReportDateFunction } from '../logic/usePaymentReportLog';
import { useGetCheckAuthorityExist } from '../../../api-refactored/user/useGetCheckAuthorityExist';
import {DateInput} from "../logic/useTransactionLog";
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  singleFormElementContainer: {
    width: '50%',
  },
  errorContainer: {
    display: 'flex',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const PaymentReportHeaderListing = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [isError, setIsError] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchTouched, setSearchTouched] = useState(false);
  const [initData, setInitData] = useState<DateInput>({
    startDate: '',
    endDate: '',
  });
  const [isFetchingTableData, setIsFetchingTableData] = useState(false);

  const handleFetchingTableData = () => {
    setIsFetchingTableData(false);
  };

  const { form } = usePaymentReportDateFunction();

  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    if ((watchStartDate && searchTouched) || (watchEndDate && searchTouched)) {
      trigger('endDate');
      // trigger('custom');
      trigger('startDate?');
    }
  }, [watchStartDate, trigger, searchTouched, watchEndDate]);

  const checkStartDate = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'Payment End Date should be greater than Payment Start Date',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('3.0')) {
        form.setError('root.custom', {
          type: 'manual',
          message: 'Search range should not be more than 3 months.',
        });
        setIsError(true);
        resetInitdata();
        return false;
      }
    }
    setIsError(false);
    return true;
  };

  /**
   * To reset the init date range when date range check failed.
   * To avoid children component getting invalid date range.
   */
  const resetInitdata = () => {
    setInitData({
      startDate: '',
      endDate: '',
    });
  };

  const checkDateRangeHandler = (formDate: SearchDate) => {
    // set init data only if date range is valid, to avoid unnecessary search API triggered
    const checkResult = checkStartDate();
    if (checkResult) {
      setInitData({
        startDate: formDate.startDate,
        endDate: formDate.endDate,
      });
      setIsFetchingTableData(true);
    }
  };

  /**
   * On page initialize, set searchTouched to true only if there is no error
   * Once set to true, isError state will be used to track API call errors
   */
  const onSearchClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setSearchTouched(true);
    }
  };
  useGetCheckAuthorityExist({
    authorityName: AUTHORITY_NAMES.CP_ADMIN_PAYMENT_REPORT_VIEW,
  });

  return (
    <Box>
      <HeaderTemplate>
        <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>PaymentReport.PaymentStartDate')}
              onChange={(date) => {
                setStartDate(date || '');
                setValue('startDate', date || '');
              }}
              control={control}
              name="startDate"
              disableFuture
              error={!!errors.startDate}
              helperText={errors?.startDate?.message?.toString()}
              inputVariant="filled"
              autoOk
            />
          </Box>

          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>PaymentReport.PaymentEndDate')}
              onChange={(date) => {
                setEndDate(date || '');
                setValue('endDate', date || '');
              }}
              control={control}
              name="endDate"
              maxDate={dayjs()}
              disableFuture
              error={!!errors.endDate}
              helperText={errors?.endDate?.message?.toString()}
              inputVariant="filled"
              autoOk
            />
          </Box>

          <Box className={classes.errorContainer}>
            <Typography className={classes.label} variant="subtitle2">
              {t('admin->>PaymentReport.DateRangeMonths', { noOfMonths: 3 })}
            </Typography>
          </Box>
          {errors?.root?.custom?.message !== '' && (
            <Box
              className={[classes.formElementContainer, classes.singleFormElementContainer].join(
                ' '
              )}
            >
              <ErrorInfo label={errors?.root?.custom?.message?.toString()} />
            </Box>
          )}

          <Box className={classes.submitButtonContainer}>
            <ApiButton
              id="button-generate"
              status={isFetchingTableData ? 'loading' : 'idle'}
              type="submit"
              onClick={onSearchClickedHandler}
            >
              {t('admin->>PaymentReport.Generate')}
            </ApiButton>
          </Box>
        </form>
      </HeaderTemplate>
      {searchTouched && !isError && (
        <ContentTemplate type="full">
          <PaymentReportSearchAndFilter
            initData={initData}
            onHandleFetchingTableData={handleFetchingTableData}
            isFetchingTableData={isFetchingTableData}
          />
        </ContentTemplate>
      )}
    </Box>
  );
};

export default PaymentReportHeaderListing;
